import { useState } from 'react';
import type { PolicyPack, PolicyRule } from '../../api/client';
import { PolicyRuleEditor } from '../PolicyRuleEditor';
import { DomainListEditor } from '../DomainListEditor';
import { PolicyTestPanel } from './PolicyTestPanel';

const EFFECTS = ['ALLOW', 'DENY', 'REQUIRE_APPROVAL', 'TRANSFORM'] as const;

const effectBadgeColors: Record<string, string> = {
  ALLOW: 'bg-[rgba(52,211,153,0.12)] text-[var(--color-success)]',
  DENY: 'bg-[rgba(248,113,113,0.12)] text-[var(--color-error)]',
  TRANSFORM: 'bg-[rgba(251,191,36,0.12)] text-[var(--color-warning)]',
  REQUIRE_APPROVAL: 'bg-[rgba(139,92,246,0.12)] text-accent-violet',
};

function emptyRule(): PolicyRule {
  return { name: '', effect: 'DENY', conditions: {} };
}

export interface PolicyStudioProps {
  policy: PolicyPack | null;
  onPolicyChange: (policy: PolicyPack) => void;
  onApply: () => void;
  applying: boolean;
}

export function PolicyStudio({ policy, onPolicyChange, onApply, applying }: PolicyStudioProps) {
  const [activeTab, setActiveTab] = useState<'test' | 'configure'>('test');

  // Empty state
  if (!policy) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-surface-2">
          <svg className="h-6 w-6 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
          </svg>
        </div>
        <p className="text-sm text-text-secondary">
          No policy generated yet. Start a conversation to build your policy.
        </p>
      </div>
    );
  }

  // Count effects
  const effectCounts: Record<string, number> = {};
  for (const rule of policy.rules) {
    effectCounts[rule.effect] = (effectCounts[rule.effect] || 0) + 1;
  }

  function updateRule(index: number, rule: PolicyRule) {
    const rules = [...policy!.rules];
    rules[index] = rule;
    onPolicyChange({ ...policy!, rules });
  }

  function deleteRule(index: number) {
    onPolicyChange({ ...policy!, rules: policy!.rules.filter((_, i) => i !== index) });
  }

  function addRule() {
    onPolicyChange({ ...policy!, rules: [...policy!.rules, emptyRule()] });
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-border-dark px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-text-primary">{policy.name}</h3>
            <div className="mt-1 flex items-center gap-2 text-xs text-text-secondary">
              <span>{policy.rules.length} rule{policy.rules.length !== 1 ? 's' : ''}</span>
              <span className="text-text-tertiary">v{policy.version}</span>
            </div>
          </div>
          <button
            onClick={onApply}
            disabled={applying}
            className="rounded-lg bg-accent-teal px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:opacity-90 disabled:opacity-50"
          >
            {applying ? 'Applying...' : 'Apply to Workspace'}
          </button>
        </div>

        {/* Effects breakdown */}
        {Object.keys(effectCounts).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(effectCounts).map(([effect, count]) => (
              <span
                key={effect}
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${effectBadgeColors[effect] || 'bg-surface-2 text-text-primary'}`}
              >
                {count} {effect}
              </span>
            ))}
          </div>
        )}

        {/* Tab toggle */}
        <div className="mt-4 flex rounded-lg bg-surface-2 p-0.5">
          <button
            onClick={() => setActiveTab('test')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'test'
                ? 'bg-surface-1 text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Test
          </button>
          <button
            onClick={() => setActiveTab('configure')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'configure'
                ? 'bg-surface-1 text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            Configure
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {activeTab === 'test' ? (
          <PolicyTestPanel policy={policy} />
        ) : (
          <div className="space-y-5">
            {/* Rules */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">
                Rules
                <span className="ml-2 text-xs font-normal text-text-tertiary">({policy.rules.length})</span>
              </h4>
              {policy.rules.map((rule, index) => (
                <PolicyRuleEditor
                  key={index}
                  rule={rule}
                  onChange={(r) => updateRule(index, r)}
                  onDelete={() => deleteRule(index)}
                />
              ))}
              <button
                type="button"
                onClick={addRule}
                className="w-full rounded-lg border border-dashed border-border-dark py-3 text-sm font-medium text-text-tertiary transition-colors hover:border-border-dark hover:text-text-secondary"
              >
                + Add Rule Manually
              </button>
            </div>

            {/* Policy Settings */}
            <details className="rounded-lg border border-border-dark bg-surface-1">
              <summary className="cursor-pointer px-5 py-3 text-sm font-semibold text-text-primary select-none">
                Policy Settings
              </summary>
              <div className="border-t border-border-dark px-5 pb-5 pt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Name</label>
                    <input
                      type="text"
                      value={policy.name}
                      onChange={(e) => onPolicyChange({ ...policy, name: e.target.value })}
                      className="w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Version</label>
                    <input
                      type="text"
                      value={policy.version}
                      onChange={(e) => onPolicyChange({ ...policy, version: e.target.value })}
                      className="w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Description</label>
                  <input
                    type="text"
                    value={policy.description || ''}
                    onChange={(e) => onPolicyChange({ ...policy, description: e.target.value || undefined })}
                    placeholder="Optional description"
                    className="w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Default Effect</label>
                  <select
                    value={policy.default_effect || 'DENY'}
                    onChange={(e) => onPolicyChange({ ...policy, default_effect: e.target.value })}
                    className="w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                  >
                    {EFFECTS.map((eff) => (
                      <option key={eff} value={eff}>{eff}</option>
                    ))}
                  </select>
                </div>
                <h4 className="text-sm font-semibold text-text-primary pt-2">Domain Lists</h4>
                <DomainListEditor
                  domains={policy.domain_allowlist || []}
                  onChange={(d) => onPolicyChange({ ...policy, domain_allowlist: d.length > 0 ? d : undefined })}
                  label="Allowlist"
                  placeholder="api.example.com"
                />
                <DomainListEditor
                  domains={policy.domain_blocklist || []}
                  onChange={(d) => onPolicyChange({ ...policy, domain_blocklist: d.length > 0 ? d : undefined })}
                  label="Blocklist"
                  placeholder="malicious.example.com"
                />
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
